import { useEffect, useState } from 'react';

/**
 * How much shorter than the window the visible area must be before it counts as
 * the on-screen keyboard. The browser's own bars coming and going move it by
 * well under this; a phone keyboard takes 250px or more.
 */
export const KEYBOARD_MIN_PX = 150;

const NOT_TYPED = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

/** A field the on-screen keyboard opens for. */
export function isEditable(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !NOT_TYPED.has(el.type) && !el.readOnly && !el.disabled;
  return el.isContentEditable === true;
}

/**
 * Whether the on-screen keyboard is up: a field has focus and the visible area
 * (visualViewport) is at least KEYBOARD_MIN_PX shorter than the window. Neither
 * iPhone Safari nor Android Chrome shrinks the window for the keyboard, only the
 * visual viewport, so the fixed bars stayed and covered what was being typed.
 *
 * While it is up, the focused field is scrolled to the middle of what is left
 * each time the visible area changes size. Off (`enabled` false) it listens to
 * nothing and stays false.
 */
export function useKeyboardOpen(enabled = true): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!enabled || !vv) {
      setOpen(false);
      return undefined;
    }
    const measure = () => {
      const el = document.activeElement;
      // Height times scale: pinch zoom shrinks the visual viewport too, and that is not a keyboard.
      const up = isEditable(el) && window.innerHeight - vv.height * vv.scale > KEYBOARD_MIN_PX;
      setOpen(up);
      return up ? el : null;
    };
    const onResize = () => {
      const el = measure();
      el?.scrollIntoView({ block: 'center' });
    };
    // On focusout the next field (if any) has not taken focus yet.
    let timer = 0;
    const onFocusChange = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 0);
    };
    vv.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);
    measure();
    return () => {
      window.clearTimeout(timer);
      vv.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusChange);
      document.removeEventListener('focusout', onFocusChange);
    };
  }, [enabled]);

  return open;
}
